/**
 * Athlete App - Highlights Page
 * Create and manage highlight reels with clips
 */

import { useState, useEffect } from 'react';
import {
  listHighlightReels,
  createHighlightReel,
  publishHighlightReel,
  deleteHighlightReel,
  getHighlightReel,
  listVideos,
  listVideoClips,
  createClip,
  addClipToReel,
  removeClipFromReel,
  aiDetectHighlights,
  type Organization,
  type HighlightReel,
  type Video,
  type VideoClip,
} from '@tss/shared';
import { Film, Plus, Share2, Eye, Clock, Scissors, X, Loader2, Check, Copy, Trash2, Video as VideoIcon, Sparkles, ChevronRight, Play } from 'lucide-react';
import { formatRelativeTime, formatDuration } from '@tss/shared';
import { PageHeader, TileGrid, HighlightTile, EmptyState, Skeleton } from '../components/UI';

interface HighlightsPageProps {
  org: Organization | null;
  proxyAthleteId?: string;
}

export function HighlightsPage({ org, proxyAthleteId }: HighlightsPageProps) {
  const [reels, setReels] = useState<HighlightReel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedReel, setSelectedReel] = useState<HighlightReel | null>(null);
  const [showClipCreator, setShowClipCreator] = useState(false);

  useEffect(() => {
    if (org) {
      loadReels();
    } else {
      setLoading(false);
    }
  }, [org]);

  async function loadReels() {
    if (!org) return;
    setLoading(true);
    try {
      const { reels: reelList } = await listHighlightReels(org.org_id);
      setReels(reelList || []);
    } catch (err) {
      console.error('Failed to load reels:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleCreateComplete() {
    setShowCreateModal(false);
    loadReels();
  }

  function handleReelUpdate() {
    loadReels();
    if (selectedReel) {
      // Reload the selected reel
      getHighlightReel(selectedReel.reel_id).then(({ reel }) => {
        setSelectedReel(reel);
      }).catch(() => {});
    }
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', width: '100%' }}>
      <PageHeader
        title="Highlights"
        description="Create and share your best moments"
        icon={Film}
        iconColor="#7c3aed"
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', fontSize: '14px' }}
          >
            <Plus size={16} />
            New Reel
          </button>
        }
      />

      {/* Quick actions */}
      <div
        className="grid gap-3 mb-5"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}
      >
        <button
          onClick={() => setShowClipCreator(true)}
          className="card card-interactive"
          style={{ padding: 'clamp(12px, 3vw, 20px)', textAlign: 'left' }}
        >
          <div
            className="icon-container icon-container-sm"
            style={{ background: 'var(--accent-bg-medium)', marginBottom: '8px' }}
          >
            <Scissors size={18} style={{ color: 'var(--accent)' }} />
          </div>
          <h3 style={{ fontWeight: '600', color: 'var(--gray-900)', marginBottom: '2px', fontSize: '14px' }}>Create Clips</h3>
          <p style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Cut clips from videos</p>
        </button>
        <button
          onClick={() => setShowClipCreator(true)}
          className="card card-interactive"
          style={{ padding: 'clamp(12px, 3vw, 20px)', textAlign: 'left' }}
        >
          <div
            className="icon-container icon-container-sm"
            style={{ background: 'var(--success-bg-light)', marginBottom: '8px' }}
          >
            <Sparkles size={18} style={{ color: 'var(--success)' }} />
          </div>
          <h3 style={{ fontWeight: '600', color: 'var(--gray-900)', marginBottom: '2px', fontSize: '14px' }}>AI Highlights</h3>
          <p style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Find best moments</p>
        </button>
      </div>

      {loading ? (
        <Skeleton.Card count={4} />
      ) : reels.length === 0 ? (
        <EmptyState.Highlights onAction={() => setShowCreateModal(true)} />
      ) : (
        <TileGrid
          items={reels}
          keyExtractor={(reel) => reel.reel_id}
          renderTile={(reel) => (
            <HighlightTile
              reel={reel}
              onClick={() => setSelectedReel(reel)}
            />
          )}
          showViewToggle={false}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateReelModal
          orgId={org?.org_id}
          onClose={() => setShowCreateModal(false)}
          onComplete={handleCreateComplete}
        />
      )}

      {/* Reel Detail Modal */}
      {selectedReel && org && (
        <ReelDetailModal
          reel={selectedReel}
          orgId={org.org_id}
          onClose={() => setSelectedReel(null)}
          onUpdate={handleReelUpdate}
        />
      )}

      {/* Clip Creator Modal */}
      {showClipCreator && org && (
        <ClipCreatorModal
          orgId={org.org_id}
          onClose={() => setShowClipCreator(false)}
          onClipCreated={loadReels}
        />
      )}
    </div>
  );
}

interface CreateReelModalProps {
  orgId?: string;
  onClose: () => void;
  onComplete: () => void;
}

function CreateReelModal({ orgId, onClose, onComplete }: CreateReelModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCreate() {
    if (!title) return;

    setCreating(true);
    setError(null);

    try {
      await createHighlightReel({
        title,
        description: description || undefined,
        org_id: orgId,
      });
      onComplete();
    } catch (err) {
      console.error('Failed to create reel:', err);
      setError(err instanceof Error ? err.message : 'Failed to create reel. Please try again.');
      setCreating(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ width: '100%', maxWidth: '420px', padding: '24px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-between" style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--gray-900)' }}>
            Create Highlight Reel
          </h2>
          <button
            onClick={onClose}
            className="btn-ghost"
            style={{ padding: '8px', boxShadow: 'none' }}
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: '12px 16px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--error)',
              marginBottom: '16px',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}

        <div className="form-group">
          <label>Reel Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Season Highlights 2024"
            disabled={creating}
          />
        </div>

        <div className="form-group">
          <label>Description (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add a description for your highlight reel..."
            rows={3}
            disabled={creating}
            style={{ resize: 'vertical' }}
          />
        </div>

        <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginBottom: '20px' }}>
          After creating, you can add clips from your videos to build your highlight reel.
        </p>

        <div className="flex gap-3">
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={creating}
            style={{ flex: 1 }}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!title || creating}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
          >
            {creating ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus size={18} />
                Create Reel
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReelCard({ reel, onUpdate, onClick }: { reel: HighlightReel; onUpdate: () => void; onClick: () => void }) {
  const [publishing, setPublishing] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const statusConfig: Record<string, { bg: string; color: string; label: string }> = {
    DRAFT: { bg: 'var(--gray-100)', color: 'var(--gray-600)', label: 'Draft' },
    PUBLISHED: { bg: 'var(--success-bg-light)', color: 'var(--success)', label: 'Published' },
    PROCESSING: { bg: 'var(--accent-bg-medium)', color: 'var(--accent)', label: 'Processing' },
  };

  const status = statusConfig[reel.status] || statusConfig.DRAFT;

  async function handlePublish(e: React.MouseEvent) {
    e.stopPropagation();
    setPublishing(true);
    try {
      await publishHighlightReel(reel.reel_id);
      onUpdate();
    } catch (err) {
      console.error('Failed to publish reel:', err);
      alert('Failed to publish reel. Please try again.');
    } finally {
      setPublishing(false);
    }
  }

  return (
    <>
      <div className="card card-interactive" style={{ overflow: 'hidden', cursor: 'pointer' }} onClick={onClick}>
        {/* Thumbnail */}
        <div
          className="flex-center"
          style={{
            aspectRatio: '16/9',
            background: 'linear-gradient(135deg, var(--primary-start) 0%, var(--primary-end) 100%)',
          }}
        >
          <Film size={32} style={{ color: 'white', opacity: 0.6 }} />
        </div>

        <div style={{ padding: '16px' }}>
          <div className="flex-between" style={{ marginBottom: '8px', gap: '8px' }}>
            <h3 className="truncate" style={{ fontWeight: '600', color: 'var(--gray-900)' }}>
              {reel.title}
            </h3>
            <span
              style={{
                padding: '3px 10px',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: '600',
                background: status.bg,
                color: status.color,
                flexShrink: 0,
              }}
            >
              {status.label}
            </span>
          </div>

          <div className="flex items-center gap-4" style={{ fontSize: '13px', color: 'var(--gray-500)', marginBottom: '12px' }}>
            <span className="flex items-center gap-1">
              <Scissors size={14} />
              {reel.clip_count || 0} clips
            </span>
            <span className="flex items-center gap-1">
              <Clock size={14} />
              {formatDuration(reel.total_duration || 0)}
            </span>
          </div>

          <div className="flex-between">
            <span style={{ fontSize: '13px', color: 'var(--gray-400)' }}>
              {formatRelativeTime(reel.created_at)}
            </span>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '13px',
                color: 'var(--accent)',
              }}
            >
              {reel.status === 'DRAFT' ? 'Edit' : 'View'}
              <ChevronRight size={16} />
            </span>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          reel={reel}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </>
  );
}

function ShareModal({ reel, onClose }: { reel: HighlightReel; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = reel.share_url || `${window.location.origin}/reel/${reel.share_token || reel.reel_id}`;

  function handleCopy() {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ width: '100%', maxWidth: '420px', padding: '24px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-between" style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--gray-900)' }}>
            Share Highlight Reel
          </h2>
          <button
            onClick={onClose}
            className="btn-ghost"
            style={{ padding: '8px', boxShadow: 'none' }}
          >
            <X size={20} />
          </button>
        </div>

        <p style={{ fontSize: '14px', color: 'var(--gray-600)', marginBottom: '16px' }}>
          Share this link with anyone to let them view your highlight reel:
        </p>

        <div
          style={{
            display: 'flex',
            gap: '8px',
            padding: '12px',
            background: 'var(--gray-50)',
            borderRadius: 'var(--radius-md)',
            marginBottom: '20px',
          }}
        >
          <input
            type="text"
            value={shareUrl}
            readOnly
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              fontSize: '13px',
              color: 'var(--gray-700)',
            }}
          />
          <button
            onClick={handleCopy}
            style={{
              padding: '8px 12px',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>

        <button
          className="btn-secondary"
          onClick={onClose}
          style={{ width: '100%' }}
        >
          Done
        </button>
      </div>
    </div>
  );
}

interface ReelDetailModalProps {
  reel: HighlightReel;
  orgId: string;
  onClose: () => void;
  onUpdate: () => void;
}

function ReelDetailModal({ reel, orgId, onClose, onUpdate }: ReelDetailModalProps) {
  const [reelData, setReelData] = useState<HighlightReel>(reel);
  const [availableClips, setAvailableClips] = useState<VideoClip[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [addingClip, setAddingClip] = useState<string | null>(null);
  const [removingClip, setRemovingClip] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [reel.reel_id]);

  async function loadData() {
    setLoading(true);
    try {
      // Load reel details and available clips
      const [reelResult, videosResult] = await Promise.all([
        getHighlightReel(reel.reel_id),
        listVideos(),
      ]);
      setReelData(reelResult.reel);

      // Get clips from all videos
      const allClips: VideoClip[] = [];
      for (const video of videosResult.videos || []) {
        try {
          const { clips } = await listVideoClips(video.video_id);
          allClips.push(...(clips || []));
        } catch {
          // Skip videos without clips
        }
      }
      setAvailableClips(allClips);
    } catch (err) {
      console.error('Failed to load reel data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handlePublish() {
    setPublishing(true);
    try {
      await publishHighlightReel(reel.reel_id);
      onUpdate();
      loadData();
    } catch (err) {
      console.error('Failed to publish:', err);
      alert('Failed to publish reel.');
    } finally {
      setPublishing(false);
    }
  }

  async function handleDelete() {
    if (!confirm('Are you sure you want to delete this reel?')) return;
    setDeleting(true);
    try {
      await deleteHighlightReel(reel.reel_id);
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Failed to delete:', err);
      alert('Failed to delete reel.');
      setDeleting(false);
    }
  }

  async function handleAddClip(clipId: string) {
    setAddingClip(clipId);
    try {
      await addClipToReel(reel.reel_id, clipId);
      loadData();
      onUpdate();
    } catch (err) {
      console.error('Failed to add clip:', err);
      alert('Failed to add clip to reel.');
    } finally {
      setAddingClip(null);
    }
  }

  async function handleRemoveClip(clipId: string) {
    setRemovingClip(clipId);
    try {
      await removeClipFromReel(reel.reel_id, clipId);
      loadData();
      onUpdate();
    } catch (err) {
      console.error('Failed to remove clip:', err);
      alert('Failed to remove clip from reel.');
    } finally {
      setRemovingClip(null);
    }
  }

  // Clips already in the reel
  const reelClipIds = new Set(reelData.clips?.map(c => c.clip_id) || []);
  const unusedClips = availableClips.filter(c => !reelClipIds.has(c.clip_id));

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: '700px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '20px', borderBottom: '1px solid var(--gray-100)' }}>
          <div className="flex-between">
            <div>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '4px' }}>
                {reelData.title}
              </h2>
              <p style={{ fontSize: '13px', color: 'var(--gray-500)' }}>
                {reelData.clip_count || 0} clips • {formatDuration(reelData.total_duration || 0)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="btn-ghost"
              style={{ padding: '8px', boxShadow: 'none' }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
          {loading ? (
            <div className="flex-center" style={{ padding: '40px' }}>
              <div
                className="animate-spin"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: '3px solid var(--gray-200)',
                  borderTopColor: 'var(--accent)',
                }}
              />
            </div>
          ) : (
            <>
              {/* Clips in Reel */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--gray-800)', marginBottom: '12px' }}>
                  Clips in Reel ({reelData.clips?.length || 0})
                </h3>
                {reelData.clips && reelData.clips.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {reelData.clips.map((clip, index) => (
                      <div
                        key={clip.clip_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          background: 'var(--gray-50)',
                          borderRadius: 'var(--radius-md)',
                        }}
                      >
                        <div
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '6px',
                            background: 'var(--accent)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: '600',
                            fontSize: '14px',
                          }}
                        >
                          {index + 1}
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontWeight: '500', color: 'var(--gray-900)', fontSize: '14px' }}>
                            {clip.title || clip.video_title || 'Untitled Clip'}
                          </p>
                          <p style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                            {formatDuration(clip.duration || 0)}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemoveClip(clip.clip_id)}
                          disabled={removingClip === clip.clip_id}
                          className="btn-ghost"
                          style={{ padding: '6px', color: 'var(--error)', boxShadow: 'none' }}
                        >
                          {removingClip === clip.clip_id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px', color: 'var(--gray-500)', background: 'var(--gray-50)', borderRadius: 'var(--radius-md)' }}>
                    <Film size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                    <p style={{ fontSize: '14px' }}>No clips in this reel yet</p>
                  </div>
                )}
              </div>

              {/* Available Clips to Add */}
              {unusedClips.length > 0 && (
                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--gray-800)', marginBottom: '12px' }}>
                    Available Clips ({unusedClips.length})
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {unusedClips.map((clip) => (
                      <div
                        key={clip.clip_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          background: 'var(--gray-50)',
                          borderRadius: 'var(--radius-md)',
                        }}
                      >
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '6px',
                            background: 'linear-gradient(135deg, var(--primary-start) 0%, var(--primary-end) 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Play size={16} style={{ color: 'white' }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontWeight: '500', color: 'var(--gray-900)', fontSize: '14px' }}>
                            {clip.title || clip.video_title || 'Untitled Clip'}
                          </p>
                          <p style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                            {formatDuration(clip.duration || 0)}
                            {clip.source === 'AI_DETECTED' && (
                              <span style={{ marginLeft: '8px', color: 'var(--success)' }}>
                                <Sparkles size={10} style={{ display: 'inline', marginRight: '2px' }} />
                                AI detected
                              </span>
                            )}
                          </p>
                        </div>
                        <button
                          onClick={() => handleAddClip(clip.clip_id)}
                          disabled={addingClip === clip.clip_id}
                          style={{
                            padding: '6px 12px',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          {addingClip === clip.clip_id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Plus size={14} />
                          )}
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {unusedClips.length === 0 && availableClips.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--gray-500)', background: 'var(--gray-50)', borderRadius: 'var(--radius-md)' }}>
                  <Scissors size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                  <p style={{ fontSize: '14px' }}>No clips available. Create clips from your videos first!</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Actions */}
        <div style={{ padding: '16px 20px', borderTop: '1px solid var(--gray-100)', display: 'flex', gap: '12px' }}>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="btn-ghost"
            style={{ color: 'var(--error)', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
            Delete
          </button>
          <div style={{ flex: 1 }} />
          {reelData.status === 'PUBLISHED' ? (
            <button
              onClick={() => setShowShareModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Share2 size={16} />
              Share
            </button>
          ) : (
            <button
              onClick={handlePublish}
              disabled={publishing || (reelData.clips?.length || 0) === 0}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              {publishing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
              Publish
            </button>
          )}
        </div>

        {showShareModal && (
          <ShareModal reel={reelData} onClose={() => setShowShareModal(false)} />
        )}
      </div>
    </div>
  );
}

interface ClipCreatorModalProps {
  orgId: string;
  onClose: () => void;
  onClipCreated: () => void;
}

function ClipCreatorModal({ orgId, onClose, onClipCreated }: ClipCreatorModalProps) {
  const [videos, setVideos] = useState<Video[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [aiDetecting, setAiDetecting] = useState(false);
  const [clipStart, setClipStart] = useState('0');
  const [clipEnd, setClipEnd] = useState('10');
  const [clipTitle, setClipTitle] = useState('');

  useEffect(() => {
    loadVideos();
  }, []);

  async function loadVideos() {
    setLoading(true);
    try {
      const { videos: videoList } = await listVideos();
      // Only show ready videos
      setVideos((videoList || []).filter(v => v.status === 'READY'));
    } catch (err) {
      console.error('Failed to load videos:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateClip() {
    if (!selectedVideo) return;

    const start = parseFloat(clipStart) || 0;
    const end = parseFloat(clipEnd) || 10;

    if (end <= start) {
      alert('End time must be greater than start time');
      return;
    }

    setCreating(true);
    try {
      await createClip({
        video_id: selectedVideo.video_id,
        org_id: orgId,
        start_time: start,
        end_time: end,
        title: clipTitle || undefined,
      });
      onClipCreated();
      setClipTitle('');
      setClipStart('0');
      setClipEnd('10');
      alert('Clip created successfully!');
    } catch (err) {
      console.error('Failed to create clip:', err);
      alert('Failed to create clip. Please try again.');
    } finally {
      setCreating(false);
    }
  }

  async function handleAIDetect() {
    if (!selectedVideo) return;

    setAiDetecting(true);
    try {
      const result = await aiDetectHighlights(selectedVideo.video_id);
      const clipCount = result.clips?.length || 0;
      alert(`AI detected ${clipCount} highlights! They've been added to your available clips.`);
      onClipCreated();
    } catch (err) {
      console.error('Failed to detect highlights:', err);
      alert('Failed to detect highlights. Please try again.');
    } finally {
      setAiDetecting(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: 'min(500px, calc(100vw - 32px))',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '20px', borderBottom: '1px solid var(--gray-100)' }}>
          <div className="flex-between">
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--gray-900)' }}>
              Create Clips
            </h2>
            <button
              onClick={onClose}
              className="btn-ghost"
              style={{ padding: '8px', boxShadow: 'none' }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
          {loading ? (
            <div className="flex-center" style={{ padding: '40px' }}>
              <div
                className="animate-spin"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: '3px solid var(--gray-200)',
                  borderTopColor: 'var(--accent)',
                }}
              />
            </div>
          ) : videos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
              <VideoIcon size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p>No videos available. Upload videos first to create clips!</p>
            </div>
          ) : (
            <>
              {/* Video Selection */}
              <div className="form-group">
                <label>Select Video</label>
                <select
                  value={selectedVideo?.video_id || ''}
                  onChange={(e) => setSelectedVideo(videos.find(v => v.video_id === e.target.value) || null)}
                >
                  <option value="">Choose a video...</option>
                  {videos.map((video) => (
                    <option key={video.video_id} value={video.video_id}>
                      {video.title}
                    </option>
                  ))}
                </select>
              </div>

              {selectedVideo && (
                <>
                  {/* AI Detect Button */}
                  <button
                    onClick={handleAIDetect}
                    disabled={aiDetecting}
                    className="btn-secondary"
                    style={{
                      width: '100%',
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    {aiDetecting ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Sparkles size={16} />
                    )}
                    {aiDetecting ? 'Detecting...' : 'AI Auto-Detect Highlights'}
                  </button>

                  <div style={{ textAlign: 'center', color: 'var(--gray-400)', margin: '16px 0', fontSize: '13px' }}>
                    — or create manually —
                  </div>

                  {/* Manual Clip Creation */}
                  <div className="form-group">
                    <label>Clip Title (optional)</label>
                    <input
                      type="text"
                      value={clipTitle}
                      onChange={(e) => setClipTitle(e.target.value)}
                      placeholder="e.g., Best play of the game"
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Start (seconds)</label>
                      <input
                        type="number"
                        value={clipStart}
                        onChange={(e) => setClipStart(e.target.value)}
                        min="0"
                        step="0.5"
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>End (seconds)</label>
                      <input
                        type="number"
                        value={clipEnd}
                        onChange={(e) => setClipEnd(e.target.value)}
                        min="0"
                        step="0.5"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleCreateClip}
                    disabled={creating}
                    style={{
                      width: '100%',
                      marginTop: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    {creating ? <Loader2 size={16} className="animate-spin" /> : <Scissors size={16} />}
                    {creating ? 'Creating...' : 'Create Clip'}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
